import { createElement, useImperativeHandle, useRef, useState, Suspense } from 'react';
import { useGetGlobalTreeNodeAndForms } from '../hooks/useGetGlobalTreeNodeAndForms';
import { useGetCustomTreeNodesAndForms } from '../hooks/useGetCustomTreeNodesAndForms';
import { useSettingsStore } from '../store/SettingsStore';
import { Splitter } from '../../Splitter';
import { Node, Tree, useTreeState } from '../../Tree';
import { Screen } from './Screen';
import { FormGroupNavigation } from '../forms/FormGroupNavigation';
import { MessageDialog, useMessageDialogText } from '../../MessageDialog';
import { useNCTextLocalizer } from '../../../locale/useNCTextLocalizer';
import { Text } from '../../Text';
import { TestIdProps, postfixTestId } from '../../../util/testId';
import { SettingsProps, useSettingsService } from '..';

import cn from './SettingsLayout.css.json';
import './SettingsLayout.css';

type TreeCommand = {
  node: Node;
  operation: { isSelected: boolean } | { isExpanded: boolean };
};

export interface SettingsLayoutProps extends TestIdProps {
  initialFocusRef?: React.Ref<{ focus(): void }>;
  customForms: SettingsProps['customForms'];
}

export const SettingsLayout = (props: SettingsLayoutProps) => {
  const treeRef = useRef<HTMLDivElement | null>(null);

  useImperativeHandle(props.initialFocusRef, () => ({
    focus() {
      treeRef.current?.focus();
    },
  }));

  const localize = useNCTextLocalizer('Settings');
  const { CONTINUE, CANCEL } = useMessageDialogText();

  const {
    writeAccessState: { hasWriteAccess },
  } = useSettingsService(); // backed by a state

  const {
    nodes: globalNodes,
    forms: globalForms,
    branches: globalBranches,
  } = useGetGlobalTreeNodeAndForms(hasWriteAccess);

  const {
    nodes: customNodes,
    forms: customForms,
    branches: customBranches,
  } = useGetCustomTreeNodesAndForms(props.customForms ?? []);

  const navigationNodes: Node[] = [...globalNodes, ...customNodes];

  const [treeCommand, setTreeCommand] = useState<TreeCommand | undefined>();

  const store = useSettingsStore();

  const [isDirty, setDirty] = useState<boolean>(false);

  const generalFormNodeId = globalNodes[0].children![0].id;

  const [activeScreen, setActiveScreen] = useState<string>(generalFormNodeId);

  const isActive = (screenId: string) => {
    return screenId === activeScreen;
  };

  const {
    nodes: treeNodes,
    expandedNodes,
    selectedNodes,
    onExpand,
    setSelectedNodes,
  } = useTreeState(
    [...globalBranches, ...customBranches].reduce(
      (expandedNodes, node) => ({ ...expandedNodes, [node.id]: true }),
      {}
    ),
    navigationNodes,
    { [generalFormNodeId]: true }
  );

  const onTreeNodeSelect = (node: Node) => {
    if (node.id !== activeScreen) {
      if (store.hasDirtyFields()) {
        setDirty(true);
        setTreeCommand({ node, operation: { isSelected: true } });
        return;
      }
      setSelectedNodes({ [node.id]: true });
      setActiveScreen(node.id);
    }
  };

  const onTreeNodeExpand = (node: Node, isExpanded: boolean) => {
    if (store.hasDirtyFields()) {
      setDirty(true);
      setTreeCommand({ node, operation: { isExpanded } });
      return;
    }
    onExpand(node, isExpanded);
    // Per UX we have to make the node being expanded/collapsed as selected too.
    setSelectedNodes({ [node.id]: true });
    setActiveScreen(node.id);
  };

  const performTreeCommand = (treeCommand: TreeCommand) => {
    const command: TreeCommand = treeCommand;
    const operation = command.operation;
    const node = command.node;

    if ('isExpanded' in operation) {
      onExpand(node, operation.isExpanded);
    }
    setSelectedNodes({ [node.id]: true });
    setActiveScreen(node.id);
  };

  const regionLandmarkLabel = [
    ...globalBranches,
    ...customBranches,
    ...[...globalForms, ...customForms].map(([node]) => node),
  ].find((node) => node.id === activeScreen)?.text;

  return (
    <div className={cn.container}>
      <MessageDialog
        testId="settings-treeNavigationAction-formInError"
        type="warning"
        isOpen={treeCommand !== undefined && isDirty}
        title={localize('settings.formNavigation.formInError.dialog.title')}
        text={localize('settings.formInError.warning.msg')}
        firstAction={{
          text: CONTINUE,
          onPress: () => {
            store.clearDirtyFields();
            performTreeCommand(treeCommand!);
            setTreeCommand(undefined);
            setDirty(false);
          },
        }}
        dismissAction={{
          text: CANCEL,
          onPress: () => {
            setTreeCommand(undefined);
            setDirty(false);
          },
          isDefault: true,
        }}
      />
      {!hasWriteAccess ? (
        <div className={cn.guestNote}>
          <Text>{localize('settings.guest.user.msg')}</Text>
        </div>
      ) : null}
      <div className={cn.contentArea}>
        <Splitter
          testId={postfixTestId(props.testId, 'splitter')}
          style={{ height: '100%', width: '100%' }}
          sections={[{ minSize: '20rem', defaultSize: '20%' }, { minSize: '20rem' }]}
        >
          <Tree
            testId={postfixTestId(props.testId, 'tree')}
            ref={treeRef}
            nodes={treeNodes}
            expandedNodes={expandedNodes}
            selectedNodes={selectedNodes}
            onSelect={onTreeNodeSelect}
            onExpand={onTreeNodeExpand}
            role="navigation"
            landmarkLabel={localize('settings.layout.tree.landmark.label')}
            nodesContainerAriaLabel={localize('settings.layout.tree.aria')}
            ariaLabel={localize('settings.layout.tree.nodeslist.aria')}
          />
          <div
            style={{ height: '100%', width: '100%' }}
            role="region"
            aria-labelledby={`${activeScreen}-header`}
            data-landmark-label={regionLandmarkLabel}
          >
            {[...globalBranches, ...customBranches]
              .map((node) => (
                <Screen key={node.id} isActive={isActive(node.id)}>
                  <FormGroupNavigation
                    id={node.id}
                    testId={postfixTestId(props.testId, node.id)}
                    heading={node.text}
                    nodes={node.children as Pick<Node, 'id' | 'text'>[]}
                    onItemPress={(routeId: string) => {
                      onExpand(node, true);
                      setSelectedNodes({ [routeId]: true });
                      setActiveScreen(routeId);
                      treeRef.current?.focus();
                    }}
                  />
                </Screen>
              ))
              .concat(
                Array.from([...globalForms, ...customForms]).map(([node, Form]) => (
                  <Screen key={node.id} isActive={isActive(node.id)}>
                    <Suspense fallback={null}>
                      <Form
                        id={node.id}
                        heading={node.text}
                        testId={postfixTestId(props.testId, node.id)}
                      />
                    </Suspense>
                  </Screen>
                ))
              )}
          </div>
        </Splitter>
      </div>
    </div>
  );
};
